import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useI18n } from '@/i18n/I18nContext';
import { LOCALE_META } from '@/i18n/translations';
import { stripLocalePath } from '@/i18n/I18nContext';

export const SEO_BASE_URL = 'https://www.sabaodyisland.com';
const OG_IMAGE = 'https://www.sabaodyisland.com/assets/hero-sabaody-bubbles.webp';

interface SEOHeadProps {
  title: string;
  description: string;
  ogImage?: string;
  type?: string;
  jsonLd?: Record<string, unknown>;
  canonicalPath?: string;
  noIndex?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  ogImage,
  type = 'website',
  jsonLd,
  canonicalPath,
  noIndex = false,
}) => {
  const location = useLocation();
  const { locale } = useI18n();
  const stripped = canonicalPath ?? stripLocalePath(location.pathname);
  const canonical = `${SEO_BASE_URL}/${locale}${stripped === '/' ? '' : stripped}`;
  const image = ogImage || OG_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,follow" />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Sabaody Island" />
      <meta property="og:locale" content={LOCALE_META[locale].htmlLang.replace('-', '_')} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default SEOHead;
